import React from 'react';
import { StyleSheet, View } from 'react-native';

import Icon from '@components/Icon';
import { Paragraph } from '@components/Typography';
import { dateRangeToString } from '@helpers/Dates';
import type { Group, Plan, UserInfo } from '@types';

export type PlanParticipantProfile = Pick<UserInfo, 'userId' | 'displayName' | 'photoUrl'>;
export type PlanGroupProfile = Pick<Group, 'groupId' | 'displayName' | 'photoUrl'>;

export type PlanDetailsProps = {
  /** Plan represented by the detail rows. */
  plan: Plan;
  /** Loaded groups associated with the plan. */
  groupProfiles?: PlanGroupProfile[];
  /** Loaded participant profiles for the plan owner and joined users. */
  participantUsers?: PlanParticipantProfile[];
  /** Icon size used by each detail row. */
  detailIconSize?: number;
  /** Maximum number of text lines for each detail row. */
  detailTextNumberOfLines?: number;
  /** Text style applied to each detail row. */
  detailTextStyle?: React.ComponentProps<typeof Paragraph>['style'];
  /** Whether to render the participant detail row. */
  showParticipants?: boolean;
  /** Container style for the detail rows. */
  style?: React.ComponentProps<typeof View>['style'];
};

type PlanDetailRowProps = {
  detailIconSize: number;
  detailTextNumberOfLines: number;
  detailTextStyle?: React.ComponentProps<typeof Paragraph>['style'];
  icon: React.ComponentProps<typeof Icon>['name'];
  testID: string;
  text: string;
};

export default function PlanDetails({
  plan,
  groupProfiles = [],
  participantUsers = [],
  detailIconSize = 16,
  detailTextNumberOfLines = 1,
  detailTextStyle,
  showParticipants = true,
  style,
}: PlanDetailsProps) {
  const participantCount = getPlanParticipantIds(plan).length;
  const participantText = getPlanParticipantText(plan, participantUsers);
  const participantIcon = participantCount > 1 ? 'account-multiple-outline' : 'account-outline';
  const groupText = groupProfiles.map((group) => group.displayName).join(', ');
  const dateRange = dateRangeToString(
    { startDate: plan.startDate, endDate: plan.endDate },
    'MMM d, yyyy'
  );

  return (
    <View style={[styles.detailContainer, style]}>
      {showParticipants ? (
        <PlanDetailRow
          detailIconSize={detailIconSize}
          detailTextNumberOfLines={detailTextNumberOfLines}
          detailTextStyle={detailTextStyle}
          icon={participantIcon}
          testID="components.PlanDetails.Text.Participants"
          text={participantText}
        />
      ) : null}
      {groupText ? (
        <PlanDetailRow
          detailIconSize={detailIconSize}
          detailTextNumberOfLines={detailTextNumberOfLines}
          detailTextStyle={detailTextStyle}
          icon="account-group-outline"
          testID="components.PlanDetails.Text.Groups"
          text={groupText}
        />
      ) : null}
      <PlanDetailRow
        detailIconSize={detailIconSize}
        detailTextNumberOfLines={detailTextNumberOfLines}
        detailTextStyle={detailTextStyle}
        icon="map-marker-outline"
        testID="components.PlanDetails.Text.Location"
        text={plan.location.displayName}
      />
      <PlanDetailRow
        detailIconSize={detailIconSize}
        detailTextNumberOfLines={detailTextNumberOfLines}
        detailTextStyle={detailTextStyle}
        icon="calendar-outline"
        testID="components.PlanDetails.Text.DateRange"
        text={dateRange}
      />
    </View>
  );
}

export function getPlanParticipantIds(plan: Plan) {
  return [...new Set([plan.userId, ...(plan.userIds || [])])];
}

export function getPlanParticipantProfiles(plan: Plan, participantUsers: PlanParticipantProfile[]) {
  const participantIds = getPlanParticipantIds(plan);
  const participantUsersById = new Map(participantUsers.map((user) => [user.userId, user]));
  return participantIds.flatMap((userId) => {
    const participantUser = participantUsersById.get(userId);
    if (participantUser) return [participantUser];
    if (userId === plan.userId) {
      return [
        {
          userId,
          displayName: plan.user.displayName,
          photoUrl: plan.user.photoUrl,
        },
      ];
    }

    return [];
  });
}

export function getPlanParticipantText(plan: Plan, participantUsers: PlanParticipantProfile[]) {
  const participantIds = getPlanParticipantIds(plan);
  const participantProfiles = getPlanParticipantProfiles(plan, participantUsers);
  const participantNames = participantProfiles.map((user) => user.displayName);
  const hiddenParticipantCount = participantIds.length - participantNames.length;

  if (hiddenParticipantCount === 0) return participantNames.join(', ');
  return `${participantNames.join(', ')} + ${hiddenParticipantCount} more`;
}

function PlanDetailRow({
  detailIconSize,
  detailTextNumberOfLines,
  detailTextStyle,
  icon,
  testID,
  text,
}: PlanDetailRowProps) {
  return (
    <View style={styles.detailRow}>
      <Icon name={icon} size={detailIconSize} style={styles.detailIcon} />
      <Paragraph
        numberOfLines={detailTextNumberOfLines}
        style={[styles.detailText, detailTextStyle]}
        testID={testID}>
        {text}
      </Paragraph>
    </View>
  );
}

const styles = StyleSheet.create({
  detailContainer: {
    gap: 1,
  },
  detailIcon: {
    flexShrink: 0,
    marginRight: 6,
  },
  detailRow: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  detailText: {
    flex: 1,
    marginVertical: 0,
  },
});
